import React, { useState } from 'react';
import { format } from 'date-fns';

function ZReportForm({ onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '00:00',
    end_time: '23:59',
    observations: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.start_date) {
      newErrors.start_date = 'La fecha de inicio es requerida';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'La fecha de fin es requerida';
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      
      if (startDate > endDate) {
        newErrors.end_date = 'La fecha de fin debe ser mayor o igual a la fecha de inicio';
      }
    }

    if (formData.start_time && formData.end_time) {
      const startTime = formData.start_time;
      const endTime = formData.end_time;
      
      if (formData.start_date === formData.end_date && startTime >= endTime) {
        newErrors.end_time = 'La hora de fin debe ser mayor a la hora de inicio para el mismo día';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Prepare data for API
      const reportData = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        observations: formData.observations.trim() || null
      };

      await onSubmit(reportData);
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          zIndex: 1040,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          zIndex: 1050,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'auto'
        }} 
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg" style={{ margin: '1.75rem auto' }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-receipt me-2"></i>
                Generar Reporte Z
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Fecha de inicio <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        disabled={loading}
                      />
                      {errors.start_date && (
                        <div className="invalid-feedback">{errors.start_date}</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Fecha de fin <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        disabled={loading}
                      />
                      {errors.end_date && (
                        <div className="invalid-feedback">{errors.end_date}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Hora de inicio</label>
                      <input
                        type="time"
                        className="form-control"
                        value={formData.start_time}
                        onChange={(e) => handleInputChange('start_time', e.target.value)}
                        disabled={loading}
                      />
                      <small className="text-muted">
                        Si no se especifica, se tomará 00:00:00
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Hora de fin</label>
                      <input
                        type="time"
                        className={`form-control ${errors.end_time ? 'is-invalid' : ''}`}
                        value={formData.end_time}
                        onChange={(e) => handleInputChange('end_time', e.target.value)}
                        disabled={loading}
                      />
                      {errors.end_time && (
                        <div className="invalid-feedback">{errors.end_time}</div>
                      )}
                      <small className="text-muted">
                        Si no se especifica, se tomará 23:59:59
                      </small>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Observaciones</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Observaciones opcionales para el reporte..."
                    value={formData.observations}
                    onChange={(e) => handleInputChange('observations', e.target.value)}
                    disabled={loading}
                  ></textarea>
                  <small className="text-muted">
                    Las observaciones aparecerán en el reporte PDF
                  </small>
                </div>

                <div className="alert alert-light-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Información:</strong>
                  <ul className="mb-0 mt-2">
                    <li>El reporte incluirá todas las ventas y pagos dentro del período especificado</li>
                    <li>Si ya existe un reporte para el mismo período, se mostrará el existente</li>
                    <li>Las fechas se toman como referencia de la fecha de creación en la base de datos</li>
                  </ul>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Generando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle me-2"></i>
                      Generar Reporte
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default ZReportForm; 